import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

const COMMON_SKILLS = [
  "Selenium", "Playwright", "Cypress", "API Testing", "Postman", "REST Assured",
  "JMeter", "K6", "TestNG", "JUnit", "Pytest", "Appium",
  "Java", "Python", "JavaScript",
  "Functional Testing", "Manual Testing", "Regression Testing", "Performance Testing",
  "Testing in Agile", "ISTQB Certification",
  "Jira", "Git", "GitHub Actions", "Jenkins", "CI/CD", "Docker",
  "AWS", "Azure", "Google Cloud",
  "SQL", "MongoDB",
  "Cursor AI", "Claude", "GitHub Copilot",
  "Cucumber", "Robot Framework",
];

const CURRENCIES = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "AED", symbol: "AED" },
  { code: "SGD", symbol: "S$" },
  { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" },
];

// Normalize numeric input: strip leading zeros, block negative
const toNonNegInt = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const cleaned = String(raw).replace(/[^\d]/g, "");
  if (cleaned === "") return "";
  return String(parseInt(cleaned, 10));
};

export default function PostJob() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  useDocumentTitle("Post a job");
  const [form, setForm] = useState({
    title: "", description: "", location: "Remote", job_type: "Full-time",
    experience_min: "0", experience_max: "8",
    required_skills: [], nice_to_have: [],
    salary_currency: "INR",
    salary_min: "",
    salary_max: "",
    salary_period: "yearly",
    salary_visible: true,
  });
  const [skillInput, setSkillInput] = useState("");
  const [niceInput, setNiceInput] = useState("");

  const addSkill = (list, item, target) => {
    const v = item.trim();
    if (!v || form[list].includes(v)) return;
    setForm((f) => ({ ...f, [list]: [...f[list], v] }));
    target("");
  };

  const removeSkill = (list, v) =>
    setForm((f) => ({ ...f, [list]: f[list].filter(x => x !== v) }));

  const submit = async () => {
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    const expMin = parseInt(form.experience_min || "0", 10);
    const expMax = parseInt(form.experience_max || "0", 10);
    if (expMax < expMin) { toast.error("Max experience must be greater than or equal to min experience"); return; }
    const salMin = form.salary_min === "" ? null : parseInt(form.salary_min, 10);
    const salMax = form.salary_max === "" ? null : parseInt(form.salary_max, 10);
    if (salMin !== null && salMax !== null && salMax < salMin) {
      toast.error("Max salary must be greater than or equal to min salary");
      return;
    }
    setSaving(true);
    try {
      const currencySymbol = CURRENCIES.find(c => c.code === form.salary_currency)?.symbol || form.salary_currency;
      const salary_range =
        salMin !== null && salMax !== null
          ? `${currencySymbol}${salMin.toLocaleString()} – ${currencySymbol}${salMax.toLocaleString()} / ${form.salary_period === "monthly" ? "month" : "year"}`
          : "";
      const payload = {
        ...form,
        experience_min: expMin,
        experience_max: expMax,
        salary_min: salMin,
        salary_max: salMax,
        salary_range,
      };
      const { data } = await api.post("/jobs", payload);
      toast.success("Job posted!");
      navigate(`/employer/job/${data.job_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to post job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">new posting</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">Post a QA job</h1>
        </div>

        <Card className="p-6 space-y-4">
          <div>
            <Label>Job title *</Label>
            <Input value={form.title} onChange={(e)=>setForm(f=>({...f, title: e.target.value}))} placeholder="Senior SDET — Java + Playwright" className="mt-1.5" data-testid="job-title" />
          </div>
          <div>
            <Label>Description *</Label>
            <Textarea
              rows={6}
              value={form.description}
              onChange={(e)=>setForm(f=>({...f, description: e.target.value}))}
              placeholder="What will the engineer do? What's the test stack?"
              className="mt-1.5"
              data-testid="job-desc"
              spellCheck={true}
              autoCorrect="on"
              autoCapitalize="sentences"
              lang="en"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e)=>setForm(f=>({...f, location: e.target.value}))} className="mt-1.5" data-testid="job-location" />
            </div>
            <div>
              <Label>Job type</Label>
              <select value={form.job_type} onChange={(e)=>setForm(f=>({...f, job_type: e.target.value}))} className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm" data-testid="job-type">
                <option>Full-time</option>
                <option>Contract</option>
                <option>Part-time</option>
                <option>Freelance</option>
              </select>
            </div>
            <div>
              <Label>Min experience (yrs)</Label>
              <Input
                type="number"
                inputMode="numeric"
                min="0"
                max="60"
                value={form.experience_min}
                onChange={(e)=>setForm(f=>({...f, experience_min: toNonNegInt(e.target.value)}))}
                onKeyDown={(e)=>{ if (["-","e","E","+"].includes(e.key)) e.preventDefault(); }}
                className="mt-1.5"
                data-testid="job-exp-min"
              />
            </div>
            <div>
              <Label>Max experience (yrs)</Label>
              <Input
                type="number"
                inputMode="numeric"
                min="0"
                max="60"
                value={form.experience_max}
                onChange={(e)=>setForm(f=>({...f, experience_max: toNonNegInt(e.target.value)}))}
                onKeyDown={(e)=>{ if (["-","e","E","+"].includes(e.key)) e.preventDefault(); }}
                className="mt-1.5"
                data-testid="job-exp-max"
              />
            </div>
          </div>

          {/* Salary section */}
          <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50/40 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <Label className="text-sm font-semibold">Salary</Label>
                <p className="text-xs text-zinc-500 mt-0.5">Add a compensation range — helps you attract better-matched candidates.</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="salary-visible"
                  checked={form.salary_visible}
                  onCheckedChange={(v)=>setForm(f=>({...f, salary_visible: v}))}
                  data-testid="salary-visible-toggle"
                />
                <Label htmlFor="salary-visible" className="text-xs cursor-pointer select-none">
                  {form.salary_visible ? "Visible to job seekers" : "Hidden from job seekers"}
                </Label>
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Currency</Label>
                <select
                  value={form.salary_currency}
                  onChange={(e)=>setForm(f=>({...f, salary_currency: e.target.value}))}
                  className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid="job-salary-currency"
                >
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Min salary</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={form.salary_min}
                  onChange={(e)=>setForm(f=>({...f, salary_min: toNonNegInt(e.target.value)}))}
                  onKeyDown={(e)=>{ if (["-","e","E","+"].includes(e.key)) e.preventDefault(); }}
                  placeholder="e.g. 500000"
                  className="mt-1.5"
                  data-testid="job-salary-min"
                />
              </div>
              <div>
                <Label className="text-xs">Max salary</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={form.salary_max}
                  onChange={(e)=>setForm(f=>({...f, salary_max: toNonNegInt(e.target.value)}))}
                  onKeyDown={(e)=>{ if (["-","e","E","+"].includes(e.key)) e.preventDefault(); }}
                  placeholder="e.g. 1500000"
                  className="mt-1.5"
                  data-testid="job-salary-max"
                />
              </div>
              <div>
                <Label className="text-xs">Period</Label>
                <select
                  value={form.salary_period}
                  onChange={(e)=>setForm(f=>({...f, salary_period: e.target.value}))}
                  className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid="job-salary-period"
                >
                  <option value="yearly">per year</option>
                  <option value="monthly">per month</option>
                </select>
              </div>
            </div>
          </div>

          <SkillRow label="Required skills"
            list={form.required_skills}
            input={skillInput} setInput={setSkillInput}
            add={(val)=>addSkill("required_skills", val ?? skillInput, setSkillInput)}
            remove={(s)=>removeSkill("required_skills", s)}
            suggestions={COMMON_SKILLS}
            testId="req"
          />

          <SkillRow label="Nice to have"
            list={form.nice_to_have}
            input={niceInput} setInput={setNiceInput}
            add={(val)=>addSkill("nice_to_have", val ?? niceInput, setNiceInput)}
            remove={(s)=>removeSkill("nice_to_have", s)}
            suggestions={COMMON_SKILLS.filter(s => !form.nice_to_have.includes(s))}
            testId="nice"
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=>navigate("/dashboard")} data-testid="cancel-job-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="publish-job-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
              {saving ? "Publishing..." : "Publish job"}
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
}

function SkillRow({ label, list, input, setInput, add, remove, suggestions, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); add(); }}} placeholder="Type a skill and press enter…" data-testid={`${testId}-skill-input`} />
        <Button onClick={add} className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid={`${testId}-add-btn`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {list.map(s => (
          <span key={s} className="tag-mono inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded">
            {s}
            <button onClick={()=>remove(s)} className="text-blue-600" data-testid={`${testId}-remove-${s}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {suggestions.slice(0, 12).filter(s => !list.includes(s)).map(s => (
          <button
            key={s}
            type="button"
            onClick={()=>add(s)}
            data-testid={`${testId}-suggest-${s}`}
            className="tag-mono inline-flex items-center gap-1 px-2 py-1 bg-white border border-zinc-200 hover:border-[#002FA7] hover:text-[#002FA7] text-zinc-700 rounded transition-colors text-xs"
          >
            <Plus className="h-3 w-3" /> {s}
          </button>
        ))}
      </div>
    </div>
  );
}
